import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import CookieConsent from "react-cookie-consent";
import "./index.css";
import { useHistory, useParams } from "react-router-dom";
import axios from "axios";

import WebcastingContext from "../context/webcasting/webcastingContext";

// COMPONENTS
import SpinnerAbsolute from "../components/UI/SpinnerAbsolute";
import NotFound from "../components/UI/NotFound";
import PendingArchiveContainer from "./PendingArchiveContainer";
import LiveLayout from "./Live";
import RegistrationContainer from "./RegistrationContainer";
import EventPasswordGate from "./EventPasswordGate";
import WebcastingPlatform from "./WebcastingPlatform";

const MainLayout = (props) => {
  const history = useHistory();
  const { confirmationId } = useParams();

  const webcastingContext = useContext(WebcastingContext);
  const {
    loading,
    event,
    authorization,
    localData,
    getEvent,
    getAuthorization,
    readLocalData,
    checkLocalDataAndRedirect,
    branding,
    validateUserID,
    userVerified,
    setNoRegID,
    noRegID,
    noRegEventPassword,
    setGA,
  } = webcastingContext;

  console.log('isEventPresent', loading)

  const [eventFounded, setEventFounded] = useState(false);

  // No-reg flow state
  const [noRegStatus, setNoRegStatus] = useState(false);
  const [noRegPassword_, setNoRegPassword] = useState(false);

  // Event password gate: which eventId has passed the password step (persisted in sessionStorage)
  const [eventPasswordVerifiedForEventId, setEventPasswordVerifiedForEventId] = useState(null);

  const eventId = props?.match?.match?.params?.eventId;
  const search = props?.match?.location?.search || "";
  const pathname = props?.location?.pathname || props?.match?.location?.pathname || "";
  const routePath = props?.match?.match?.path || "";

  const eventItem = event;
  const hasEventItem = Boolean(eventItem);

  const isQueryBypassMode = useMemo(() => {
    return (
      search.includes("previewKey=") ||
      search.includes("ispresenter=off") ||
      search.includes("ispresenter=on") ||
      search.includes("template=clean") ||
      search.includes("controlbar=no") ||
      search.includes("controlbar=yes") ||
      search.includes("reg=off")
    );
  }, [search]);

  const isPreviewMode = useMemo(() => search.includes("previewKey="), [search]);
  const isVerifiedInQuery = useMemo(() => search.includes("isverified=true"), [search]);
  const isTemplateClean = useMemo(() => search.includes("template=clean"), [search]);
  const isRegOff = useMemo(() => search.includes("reg=off"), [search]);

  // -------------------------
  // No-reg localStorage helpers
  // -------------------------
  const checkLocalDataAndRedirectForNoReg = useCallback(() => {
    const raw = localStorage.getItem("noReglocalData");
    if (!raw) return [];

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }

    const events = parsed?.user?.events || [];
    return events.filter((e) => e?.eventID === eventId);
  }, [eventId]);

  const saveLocalDataAndRedirectForNoReg = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_STATS_API_URL}/event/generateUniqueSessionUID/?token=${process.env.REACT_APP_STATS_API_TOKEN}`
      );

      const sessionUID = res?.data;

      // ensure initial structure exists
      if (!localStorage.getItem("noReglocalData")) {
        localStorage.setItem(
          "noReglocalData",
          JSON.stringify({
            user: { events: [] },
          })
        );
      }

      const raw = localStorage.getItem("noReglocalData");
      const parsed = raw ? JSON.parse(raw) : { user: { events: [] } };

      parsed.user = parsed.user || { events: [] };
      parsed.user.events = parsed.user.events || [];

      parsed.user.events.push({
        eventID: eventId,
        sessionUID,
      });

      localStorage.setItem("noReglocalData", JSON.stringify(parsed));

      setNoRegID(sessionUID);
      setNoRegStatus(true);
    } catch (error) {
      // original behaviour: still allow UI to continue
      setNoRegStatus(true);
    }
  }, [eventId, setNoRegID]);

  const getTokenAndSaveNoReglocal = useCallback(async () => {
    const found = checkLocalDataAndRedirectForNoReg();

    if (found?.length > 0) {
      const sessionUID = found[0]?.sessionUID;
      if (sessionUID) setNoRegID(sessionUID);
      setNoRegStatus(true);
      return;
    }

    await saveLocalDataAndRedirectForNoReg();
  }, [checkLocalDataAndRedirectForNoReg, saveLocalDataAndRedirectForNoReg, setNoRegID]);

  // Keep this small debug effect (as in your original file)
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("GOT UID from context", noRegID);
  }, [noRegID]);

  useEffect(() => {
    setNoRegPassword(noRegEventPassword);
  }, [noRegEventPassword]);

  // Restore or reset event password verification when eventId changes
  useEffect(() => {
    if (!eventId) {
      setEventPasswordVerifiedForEventId(null);
      return;
    }
    try {
      if (sessionStorage.getItem(`eventPasswordVerified_${eventId}`) === "1") {
        setEventPasswordVerifiedForEventId(eventId);
      } else {
        setEventPasswordVerifiedForEventId(null);
      }
    } catch {
      setEventPasswordVerifiedForEventId(null);
    }
  }, [eventId]);

  // -------------------------
  // Fetch event + local redirect + validate user
  // -------------------------
  useEffect(() => {
    getEvent(eventId);

    if (isQueryBypassMode) return;

    // preserve original heuristic
    if (pathname.length === 35) {
      checkLocalDataAndRedirect(eventId);
    }

    // preserve original behavior (validate only when confirmationId exists and not already verified via query)
    if (confirmationId && !isVerifiedInQuery) {
      validateUserID(eventId, confirmationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, userVerified]);

  // -------------------------
  // Host + event_creator enforcement + no-reg session for non-preview
  // -------------------------
  useEffect(() => {
    if (!eventFounded || !hasEventItem) return;

    const hostName = window.location.hostname;

    // NOTE: Your original code had an impossible condition:
    // event_creator === 'engageinvestor' && === 'emperor' && === 'buchanan'
    // This refactor makes it functional by treating it as “creator is in a set”.
    const creator = eventItem?.event_creator;

    const deny = (hostContains, creatorsNotAllowed = []) => {
      if (!hostName.includes(hostContains)) return false;
      return creatorsNotAllowed.includes(creator);
    };

    const shouldRedirectNotAuthorized =
      deny("brrmedia", ["engageinvestor", "emperor", "buchanan"]) ||
      deny("buchanan", ["brr", "emperor", "engageinvestor"]) ||
      deny("emperor", ["brr", "buchanan", "engageinvestor"]) ||
      deny("engageinvestor", ["buchanan", "emperor"]);

    if (shouldRedirectNotAuthorized) {
      history.push("/NotAuthorized");
      return;
    }

    // Start logic for NoReg Event (skip previews)
    if (!isPreviewMode && !routePath.includes("preview")) {
      getTokenAndSaveNoReglocal();
      // eslint-disable-next-line no-console
      console.log("Registration????", eventItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFounded, hasEventItem, eventItem, routePath, isPreviewMode]);

  useEffect(() => {
    if (!eventItem || !event) return;
    setEventFounded(true);
  }, [event, eventItem]);

  useEffect(() => {
    readLocalData(event, authorization);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, authorization]);

  useEffect(() => {
    const cid = props?.match?.match?.params?.confirmationId;
    if (cid) {
      getAuthorization(eventId, cid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.match?.match?.params?.confirmationId, eventId]);

  // -------------------------
  // Auth helpers
  // -------------------------
  const checkAuthEvent = useCallback(
    (e) => e?.eventId === eventId && Boolean(e?.auth),
    [eventId]
  );

  // -------------------------
  // Small render helpers
  // -------------------------
  const renderLiveOrPlatform = useCallback(
    ({ saveAuth, preview = false }) => {
      if (!hasEventItem) return <SpinnerAbsolute />;

      return eventItem.isLive ? (
        <LiveLayout
          match={props.match}
          loading={loading}
          event={event}
          saveAuth={saveAuth}
          authorization={props?.match?.match?.params?.confirmationId}
          localData={localData}
          preview={preview}
        />
      ) : (
        <WebcastingPlatform
          match={props.match}
          loading={loading}
          event={event}
          saveAuth={saveAuth}
          authorization={props?.match?.match?.params?.confirmationId}
          localData={localData}
          preview={preview}
          branding={branding}
        />
      );
    },
    [hasEventItem, eventItem, props.match, loading, event, localData, branding]
  );

  // -------------------------
  // EARLY RETURNS / STATE GATES
  // -------------------------

  // Preview mode: bypass LiveLayout, status checks, and socket triggers entirely —
  // just render the platform layout directly.
  if (isPreviewMode) {
    if (!hasEventItem) return <SpinnerAbsolute />;
    return (
      <WebcastingPlatform
        match={props.match}
        loading={loading}
        event={event}
        saveAuth={false}
        authorization={props?.match?.match?.params?.confirmationId}
        localData={localData}
        preview={true}
        branding={branding}
      />
    );
  }

  // Loading / missing data guard
  if (loading) {
    return <SpinnerAbsolute />;
  }

  const eventNotFound =
    !eventItem || Array.isArray(eventItem) || !eventItem?._id;
  if (eventNotFound) {
    return <NotFound msg="event not found" />;
  }

  // no-reg local flow needs to be ready before continuing
  if (!noRegStatus) return null;

  if (event.success === false) {
    return <NotFound msg={event?.message?.message} />;
  }

  if (!event) {
    return <NotFound msg="event not found" />;
  }

  // Status guards
  if (
    eventItem.status === "pending_approval" ||
    eventItem.status === "closed_group2"
  ) {
    return <NotFound msg="Event in pending approval" />;
  }

  if (eventItem.status === "pending_archive") {
    return (
      <PendingArchiveContainer
        match={props.match}
        loading={loading}
        event={event}
        saveAuth={true}
        authorization={props?.match?.match?.params?.confirmationId}
        localData={localData}
        branding={branding}
      />
    );
  }

  // No-reg render branch
  const allowNoRegFlow = isRegOff || (noRegPassword_ === true && !eventItem.registrationRequired);
  if (allowNoRegFlow) {
    return (
      <>
        {renderLiveOrPlatform({ saveAuth: isRegOff ? false : true, preview: Boolean(props.preview) })}
        {/* Cookie banners below (kept consistent with your original logic) */}
        {!isTemplateClean && !eventItem.registrationRequired && (
          <CookieConsent
            enableDeclineButton
            buttonText="Accept"
            declineButtonStyle={{ background: "white", color: "black" }}
            buttonStyle={{ background: "var(--clr-brr)", color: "white" }}
            declineButtonText="Decline"
            onAccept={() => setGA(true)}
            onDecline={() => alert("nay!")}
            cookieName="mySiteCookieConsent"
            location="top"
            style={{ background: "#e3e3e3", color: "#2B373B", zIndex: 99000 }}
          >
            We use cookies to provide and improve our services. By using our site you consent to{" "}
            <a href="../../../../../cookies" target="_blank" rel="noreferrer">
              cookies
            </a>
          </CookieConsent>
        )}

        {eventItem.registrationRequired && eventItem._id !== "6617f9d68d18254c64eac445" && (
          <CookieConsent
            cookieName="mySiteCookieConsent"
            location="top"
            style={{ background: "#e3e3e3", color: "#2B373B", zIndex: 99000 }}
          >
            We use cookies to provide and improve our services. By using our site you consent to{" "}
            <a href="../../../../../cookies" target="_blank" rel="noreferrer">
              cookies
            </a>
          </CookieConsent>
        )}
      </>
    );
  }

  // Must have eventFounded to proceed
  if (!eventFounded) {
    return <SpinnerAbsolute />;
  }

  // -------------------------
  // MAIN AUTH / AGM / REG FLOW (cleaned but same outcomes)
  // -------------------------
  const needsGate = Boolean(eventItem.registrationRequired);

  const renderGateFlow = () => {
    // When there is no localData yet, show spinner (same as original)
    if (!localData) {
      console.log("[MainLayout] Rendering: SpinnerAbsolute (no localData)");
      return <SpinnerAbsolute />;
    }

    const isAllowed = localData.user?.events?.some(checkAuthEvent);

    if (isAllowed) {
      console.log("[MainLayout] Rendering: Live/Platform (isAllowed from localData)");
      return renderLiveOrPlatform({ saveAuth: true, preview: false });
    }

    // If already authorized, allow
    if (authorization?.success) {
      console.log("[MainLayout] Rendering: Live/Platform (authorization.success)");
      return renderLiveOrPlatform({ saveAuth: true, preview: false });
    }

    // Event password gate is handled in mainContent (before renderGateFlow) so it applies even when registrationRequired is false
    console.log("[MainLayout] Rendering: RegistrationContainer");
    return (
      <RegistrationContainer
        match={props.match}
        loading={loading}
        event={event}
        saveAuth={false}
      />
    );
  };

  // Password protection is independent of registration: check first even when registrationRequired is false
  const needsPasswordGate =
    Boolean(eventItem?.passwordProtection) && eventPasswordVerifiedForEventId !== eventId;

  const mainContent = (() => {
    if (needsPasswordGate) {
      console.log("[MainLayout] Rendering: EventPasswordGate (password protected event)");
      return (
        <EventPasswordGate
          event={eventItem}
          eventId={eventId}
          onSuccess={() => {
            setEventPasswordVerifiedForEventId(eventId);
            try {
              sessionStorage.setItem(`eventPasswordVerified_${eventId}`, "1");
            } catch (_) {}
          }}
        />
      );
    }
    if (!needsGate) {
      console.log("[MainLayout] Rendering: Live/Platform (no registration required)");
      return renderLiveOrPlatform({ saveAuth: true, preview: false });
    }
    return renderGateFlow();
  })();

  return (
    <>
      {mainContent}

      {!isTemplateClean && !eventItem.registrationRequired && (
        <CookieConsent
          enableDeclineButton
          buttonText="Accept"
          declineButtonStyle={{ background: "white", color: "black" }}
          buttonStyle={{ background: "var(--clr-brr)", color: "white" }}
          declineButtonText="Decline"
          onAccept={() => setGA(true)}
          onDecline={() => alert("nay!")}
          cookieName="mySiteCookieConsent"
          location="top"
          style={{ background: "#e3e3e3", color: "#2B373B", zIndex: 99000 }}
        >
          We use cookies to provide and improve our services. By using our site you consent to{" "}
          <a href="../../../../../cookies" target="_blank" rel="noreferrer">
            cookies
          </a>
        </CookieConsent>
      )}

      {eventItem.registrationRequired && eventItem._id !== "6617f9d68d18254c64eac445" && (
        <CookieConsent
          cookieName="mySiteCookieConsent"
          location="top"
          style={{ background: "#e3e3e3", color: "#2B373B", zIndex: 99000 }}
        >
          We use cookies to provide and improve our services. By using our site you consent to{" "}
          <a href="../../../../../cookies" target="_blank" rel="noreferrer">
            cookies
          </a>
        </CookieConsent>
      )}
    </>
  );
};

export default React.memo(MainLayout);
